/**
 * Channel API. All channel-related HTTP calls go through here.
 */
import { getApiClient } from "./client";
import AuthTeams from "../../auth/AuthTeams";
import { logger } from "../../utils/logger";

/**
 * Resolves group/channel ids the API expects (private channels use ownerGroupId).
 * @param {object} [teamsContext]
 * @returns {{ groupId: string, channelId: string }}
 */
export function resolveChannelIdentifiers(teamsContext) {
  const channelRaw = teamsContext?.channel?.id ?? "";
  const channelId = typeof channelRaw === "string" ? channelRaw.trim() : "";
  if (!channelId) {
    return { groupId: "", channelId: "" };
  }
  const groupIdRaw =
    teamsContext.channel?.membershipType === "Private"
      ? teamsContext.channel.ownerGroupId ?? ""
      : teamsContext.team?.groupId ?? "";
  const groupId = String(groupIdRaw ?? "").trim();
  return { groupId, channelId };
}

/**
 * Calls the backend to check if the current channel is the team's primary (General) channel.
 * @param {object} teamsContext
 * @param {string} accessToken
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function checkIsPrimaryChannel(teamsContext, accessToken) {
  logger.info("checkIsPrimaryChannel", null, "API");
  try {
    const client = getApiClient(accessToken);
    const { groupId, channelId } = resolveChannelIdentifiers(teamsContext);
    const url = `api/Channel/check-primary-channel/${encodeURIComponent(groupId)}/${encodeURIComponent(channelId)}`;
    const res = await client.get(url);
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("checkIsPrimaryChannel failed", err, "API");
    throw err;
  }
}

/** True when get-channel-configs can be called with non-empty query ids. */
export function hasChannelIdentifiersForApi(teamsContext) {
  const { groupId, channelId } = resolveChannelIdentifiers(teamsContext);
  return !!(groupId && channelId);
}

/**
 * @param {object} teamsContext - Teams context (user, channel, team)
 * @param {string} accessToken - Bearer token
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function getChannelConfigs(teamsContext, accessToken) {
  logger.info("getChannelConfigs", null, "API");
  try {
    const client = getApiClient(accessToken);
    const { groupId, channelId } = resolveChannelIdentifiers(teamsContext);
    const url =
      `api/Channel/get-channel-configs?webUser=${encodeURIComponent(new AuthTeams().getEmail())}` +
      `&tenantId=${teamsContext.user?.tenant?.id ?? ""}` +
      `&groupId=${groupId}&channelId=${channelId}`;
    const res = await client.get(url, { timeout: 0 });
    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("getChannelConfigs failed", err, "API");
    throw err;
  }
}

/**
 * @param {object} teamsContext - Teams context
 * @param {string} accessToken - Bearer token
 * @param {Array} objectConnections - Array of object connection objects
 * @returns {Promise<{ data: any, status: number }>}
 */
export async function saveChannelConfiguration(teamsContext, accessToken, objectConnections) {
  logger.info("saveChannelConfiguration", null, "API");
  try {
    const client = getApiClient(accessToken);
    const { groupId, channelId } = resolveChannelIdentifiers(teamsContext);

    const url =
      `api/Channel/update-channel-description/${encodeURIComponent(groupId)}/${encodeURIComponent(channelId)}` +
      `?webUser=${encodeURIComponent(new AuthTeams().getEmail())}` +
      `&tenantId=${teamsContext.user?.tenant?.id ?? ""}`;
    
    const res = await client.patch(url, { objectConnections }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return { data: res.data, status: res.status };
  } catch (err) {
    logger.error("saveChannelConfiguration failed", err, "API");
    throw err;
  }
}
